import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { User, Phone, Mail, LogOut, Edit, Globe, Shield, Key, Moon, Sun, ChevronDown, Settings, Check, X, Bell, Lock, HelpCircle, ExternalLink, Menu, ChevronRight, AlertCircle, Activity, Heart, MapPin, Plus, Navigation, FileText, Star, BarChart } from 'lucide-react';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import QRCode from '../../components/QRCode';
import BottomNavigation from '../../components/BottomNavigation';
import ProfileMenu from '../../components/Profile/ProfileMenu';
import StatisticsPanel from '../../components/Stats/StatisticsPanel';
import SecurityVerification from '../../components/SecurityVerification';
import withTranslation from '../../hoc/withTranslation';
// Enhanced Utils imports for better functionality
import { 
  formatPhoneNumber, 
  formatDate, 
  formatUserProfile, 
  getDefaultUserData, 
  getDefaultEmergencyContacts,
  truncateText,
  isValidBloodType
} from '../../utils/formatters';
import { 
  isValidEmail, 
  isValidPhoneNumber, 
  validateProfileData,
  sanitizeInput 
} from '../../utils/validation';
import { 
  saveToStorage, 
  getFromStorage,
  saveUserPreferences,
  getUserPreferences 
} from '../../utils/storage';

const ProfileScreen = ({ t, currentLanguage }) => {
  const { navigateToScreen, pullDownProgress, showPullDownMenu, handleTouchStart, handleTouchMove, handleTouchEnd, screenParams } = useUI();
  const { user, logout } = useAuth();
  const { languages, changeLanguage } = useLanguage();

  // Debug: Log user changes
  useEffect(() => {
    console.log('🔄 ProfileScreen: User data updated:', user);
  }, [user]);

  // Force re-render when user data changes
  useEffect(() => {
    if (user) {
      console.log('👤 User profile updated in ProfileScreen:', {
        name: user.name,
        username: user.username,
        profilePhoto: user.profilePhoto ? 'exists' : 'missing'
      });
    }
  }, [user?.name, user?.username, user?.profilePhoto]);

  // Listen for profile updates from ProfileEditScreen
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      console.log('🔄 ProfileScreen: Received profile update event:', event.detail);
      // Force re-render by updating a state variable
      setActiveTab(prev => prev); // This triggers a re-render without changing the actual tab
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);
  
  // UI states
  const [activeTab, setActiveTab] = useState('identity');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showAppearanceModal, setShowAppearanceModal] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showSecurity, setShowSecurity] = useState(false);
  
  // Theme support
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Animation and effect states
  const [reducedMotion, setReducedMotion] = useState(() => {
    return localStorage.getItem('reducedMotion') === 'true';
  });

  // Font size settings
  const [fontSizeScale, setFontSizeScale] = useState(() => {
    return parseFloat(localStorage.getItem('fontSizeScale') || '0.8');
  });

  // References
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  
  // Enhanced theme configuration with better color schemes and contrast
  const themeConfig = useMemo(() => ({
    dark: {
      bgColor: 'bg-gray-900',
      textColor: 'text-gray-100', // Daha açık metin rengi
      textSecondary: 'text-gray-200', // Daha açık ikincil metin
      textMuted: 'text-gray-300', // Daha açık soluk metin
      cardBg: 'bg-gray-800',
      cardBorder: 'border-gray-600', // Daha açık kenarlık
      dividerColor: 'border-gray-600',
      headerBg: 'bg-gray-800',
      buttonBg: 'bg-blue-600', // Daha açık mavi
      buttonHover: 'hover:bg-blue-500',
      buttonSecondary: 'bg-gray-700 hover:bg-gray-600',
      buttonDanger: 'bg-red-600 hover:bg-red-500',
      buttonSuccess: 'bg-green-600 hover:bg-green-500',
      editButton: 'bg-white/20 hover:bg-white/30 text-white',
      badgeColors: {
        red: 'bg-red-500/20 text-red-200 border border-red-400/30',
        blue: 'bg-blue-500/20 text-blue-200 border border-blue-400/30',
        indigo: 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30',
        teal: 'bg-teal-500/20 text-teal-200 border border-teal-400/30',
        green: 'bg-green-500/20 text-green-200 border border-green-400/30'
      },
      iconColor: 'text-blue-400',
      linkColor: 'text-blue-400 hover:text-blue-300'
    },
    light: {
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-900', // Daha koyu metin
      textSecondary: 'text-gray-800', // Daha koyu ikincil metin
      textMuted: 'text-gray-600',
      cardBg: 'bg-white',
      cardBorder: 'border-gray-200',
      dividerColor: 'border-gray-200',
      headerBg: 'bg-white',
      buttonBg: 'bg-blue-600',
      buttonHover: 'hover:bg-blue-700',
      buttonSecondary: 'bg-gray-200 hover:bg-gray-300',
      buttonDanger: 'bg-red-600 hover:bg-red-700',
      buttonSuccess: 'bg-green-600 hover:bg-green-700',
      editButton: 'bg-black/10 hover:bg-black/20 text-white',
      badgeColors: {
        red: 'bg-red-100 text-red-800 border border-red-200',
        blue: 'bg-blue-100 text-blue-800 border border-blue-200',
        indigo: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
        teal: 'bg-teal-100 text-teal-800 border border-teal-200',
        green: 'bg-green-100 text-green-800 border border-green-200'
      },
      iconColor: 'text-blue-600',
      linkColor: 'text-blue-600 hover:text-blue-700'
    }
  }), []);
  
  const currentTheme = themeConfig[isDarkMode ? 'dark' : 'light'];
  
  // Tab items with translation function
  const tabList = [
    { id: 'identity', label: t('profile.identity', 'Kimlik') },
    { id: 'statistics', label: t('profile.statistics', 'İstatistikler') },
    { id: 'settings', label: t('settings.appSettings', 'Uygulama Ayarları') }
  ];

  // Theme toggle function
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const newValue = !prev;
      localStorage.setItem('theme', newValue ? 'dark' : 'light');
      return newValue;
    });
  }, []);
  
  // Toggle animation preferences
  const toggleReducedMotion = useCallback(() => {
    setReducedMotion(prev => {
      const newValue = !prev;
      localStorage.setItem('reducedMotion', String(newValue));
      return newValue;
    });
  }, []);
  
  // Font size changer
  const handleFontSizeChange = useCallback((scale) => {
    setFontSizeScale(scale);
    localStorage.setItem('fontSizeScale', scale.toString());
    document.documentElement.style.setProperty('--font-size-scale', scale);
  }, []);

  // Apply scale settings on initial load
  useEffect(() => {
    document.documentElement.style.setProperty('--font-size-scale', fontSizeScale);
  }, [fontSizeScale]);

  // Handle URL parameters for automatic tab switching
  useEffect(() => {
    if (screenParams && screenParams.tab) {
      const targetTab = screenParams.tab;
      // Map valid tabs
      const validTabs = ['identity', 'statistics', 'settings'];
      if (validTabs.includes(targetTab)) {
        setActiveTab(targetTab);
      }
    }
  }, [screenParams]);

  // Open/close profile menu
  const toggleProfileMenu = useCallback(() => {
    setShowProfileMenu(prev => !prev);
  }, []);

  // Logout function
  const handleLogout = () => {
    logout();
    navigateToScreen('welcome');
  };

  // Navigate to dashboard on profile photo click
  const handleProfilePhotoClick = () => {
    navigateToScreen('dashboard');
  };
  
  // Get current language object
  const getCurrentLanguageObj = () => {
    return languages.find(l => l.code === currentLanguage) || languages[0];
  };
  
  // Current language info
  const currentLang = getCurrentLanguageObj();

  // Uygulama ayarları modalını açma (yeni)
  const handleAppSettings = useCallback((tab = null) => {
    if (tab === 'digital_discipline') {
      navigateToScreen('settings?tab=digital_discipline');
    } else {
      navigateToScreen('settings');
    }
  }, [navigateToScreen]);

  // Hesap ayarları ekranına yönlendirme (Profil düzenleme)
  const handleAccountSettings = useCallback(() => {
    try {
      console.log('🔄 ProfileScreen: Navigating to profile edit from account settings (section: general)...');
      navigateToScreen('profileEdit', { section: 'general' });
    } catch (error) {
      console.error('❌ Navigation error for account settings:', error);
      navigateToScreen('profileEdit');
    }
  }, [navigateToScreen]);

  // Güvenlik ayarları ekranına yönlendirme
  const handleSecuritySettings = useCallback(() => {
    navigateToScreen('securitySettings');
  }, [navigateToScreen]);

  // Dil ve bölge ayarları ekranına yönlendirme
  const handleLanguageAndRegion = useCallback(() => {
    navigateToScreen('languageAndRegion');
  }, [navigateToScreen]);

  // Yardım ve destek ekranına yönlendirme
  const handleHelpAndSupport = useCallback(() => {
    navigateToScreen('help');
  }, [navigateToScreen]);

  // Çıkış onaylama işlevi
  const handleConfirmLogout = useCallback(() => {
    try {
      // Oturum verilerini temizle
      saveUserPreferences('lastLogoutTime', Date.now());
      
      // Çıkış işlemini gerçekleştir
      logout();
      
      // Hoş geldin ekranına yönlendir
      navigateToScreen('welcome');
      
      // Modal'ı kapat
      setShowLogoutConfirm(false);
    } catch (error) {
      console.error('Logout error:', error);
      // Hata durumunda da çıkış yapmaya çalış
      logout();
      navigateToScreen('welcome');
    }
  }, [logout, navigateToScreen, saveUserPreferences]);

  // Add functionality to the "Edit Profile" button
  const handleEditProfile = useCallback(() => {
    try {
      navigateToScreen('profileEdit');
    } catch (error) {
      console.error('Navigation error to profileEdit:', error);
    }
  }, [navigateToScreen]);

  // Enhanced Security Verification Handler with comprehensive error handling
  const handleShowSecurityVerification = useCallback((onSuccess) => {
    try {
      // Log security verification attempt for audit purposes
      console.log('Security verification requested:', new Date().toISOString());
      
      // Validate user authentication state
      if (!user?.id) {
        console.warn('User not authenticated for security verification');
        navigateToScreen('login');
        return;
      }

      // Save current context for post-verification restoration
      saveToStorage('pendingSecurityAction', {
        timestamp: Date.now(),
        action: 'security_verification',
        userId: user.id
      });

      // Enhanced security verification with callback
      setShowSecurity(true);
      
      // Store the success callback securely
      if (typeof onSuccess === 'function') {
        window._securityVerificationCallback = onSuccess;
      }
      
      // Auto-hide security modal after timeout (security measure)
      setTimeout(() => {
        if (showSecurity) {
          console.log('Security verification timeout - auto-closing modal');
          setShowSecurity(false);
          window._securityVerificationCallback = null;
        }
      }, 300000); // 5 minutes timeout
      
    } catch (error) {
      console.error('Security verification initialization failed:', error);
      // Fallback - allow action without verification in case of system error
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    }
  }, [user, showSecurity, navigateToScreen]);

  // Enhanced navigation handlers with proper error handling
  const handleEditEmergencyInfo = useCallback(() => {
    handleShowSecurityVerification(() => {
      try {
        console.log('🔄 ProfileScreen: Navigating to edit emergency info (section: emergency)...');
        navigateToScreen('profileEdit', { section: 'emergency' });
      } catch (error) {
        console.error('❌ Navigation error for emergency edit:', error);
        navigateToScreen('profileEdit');
      }
    });
  }, [handleShowSecurityVerification, navigateToScreen]);

  const handleEditEmergencyContacts = useCallback(() => {
    handleShowSecurityVerification(() => {
      try {
        console.log('🔄 ProfileScreen: Navigating to edit emergency contacts (section: emergency)...');
        navigateToScreen('profileEdit', { section: 'emergency' });
      } catch (error) {
        console.error('❌ Navigation error for contacts edit:', error);
        navigateToScreen('profileEdit');
      }
    });
  }, [handleShowSecurityVerification, navigateToScreen]);

  const handleAddEmergencyContact = useCallback(() => {
    handleShowSecurityVerification(() => {
      try {
        console.log('🔄 ProfileScreen: Navigating to add emergency contact (section: emergency)...');
        navigateToScreen('profileEdit', { section: 'emergency' });
      } catch (error) {
        console.error('❌ Navigation error for add contact:', error);
        navigateToScreen('profileEdit');
      }
    });
  }, [handleShowSecurityVerification, navigateToScreen]);

  const handleEditPersonalInfo = useCallback(() => {
    try {
      console.log('🔄 ProfileScreen: Navigating to edit personal info (section: general)...');
      navigateToScreen('profileEdit', { section: 'general' });
    } catch (error) {
      console.error('❌ Navigation error for personal info edit:', error);
      navigateToScreen('profileEdit');
    }
  }, [navigateToScreen]);

  const handleEditPhysicalInfo = useCallback(() => {
    try {
      console.log('🔄 ProfileScreen: Navigating to edit physical info (section: physical)...');
      navigateToScreen('profileEdit', { section: 'physical' });
    } catch (error) {
      console.error('❌ Navigation error for physical info edit:', error);
      navigateToScreen('profileEdit');
    }
  }, [navigateToScreen]);

  // Render appearance modal
  const renderAppearanceModal = () => showAppearanceModal && (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-sm w-full p-6`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('settings.themeAndAppearance', 'Theme and Appearance')}</h3>
          <button 
            className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            onClick={() => setShowAppearanceModal(false)}
          >
            <X size={20} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
          </button>
        </div>
        <div className="space-y-6">
          {/* Theme Selection */}
          <div>
            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('settings.colorTheme', 'Color Theme')}</h4>
            <div className="flex space-x-4">
              <button 
                className={`flex-1 p-4 rounded-xl ${!isDarkMode ? 'bg-blue-100 border-2 border-blue-500' : 'bg-white/5 border border-gray-700'}`}
                onClick={() => { if (isDarkMode) toggleTheme(); }}
              >
                <div className="h-16 bg-white rounded-lg mb-2 shadow-sm"></div>
                <p className={`text-sm text-center ${isDarkMode ? 'text-gray-200' : 'text-gray-800 font-medium'}`}>{t('settings.lightMode', 'Light')}</p>
              </button>
              <button 
                className={`flex-1 p-4 rounded-xl ${isDarkMode ? 'bg-indigo-900/30 border-2 border-indigo-500' : 'bg-gray-100 border border-gray-300'}`}
                onClick={() => { if (!isDarkMode) toggleTheme(); }}
              >
                <div className="h-16 bg-gray-800 rounded-lg mb-2 shadow-sm"></div>
                <p className={`text-sm text-center ${isDarkMode ? 'text-white font-medium' : 'text-gray-700'}`}>{t('settings.darkMode', 'Dark')}</p>
              </button>
            </div>
          </div>
          {/* Font Size Accessibility */}
          <div>
            <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Font Size</h4>
            <div className="flex space-x-2">
              {[0.8, 1, 1.15, 1.3].map(scale => (
                <button
                  key={scale}
                  onClick={() => handleFontSizeChange(scale)}
                  className={`flex-1 py-2 rounded-lg ${fontSizeScale === scale ? (isDarkMode ? 'bg-indigo-600 text-white' : 'bg-blue-600 text-white') : (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800')}`}
                >
                  {scale === 0.8 ? 'Small' : scale === 1 ? 'Normal' : scale === 1.15 ? 'Large' : 'Extra Large'}
                </button>
              ))}
            </div>
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Increase font size for better readability.</p>
          </div>
          <button
            className={`w-full mt-4 py-3 rounded-lg font-medium ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'} transition-colors`}
            onClick={() => setShowAppearanceModal(false)}
          >
            {t('common.done', 'Done')}
          </button>
        </div>
      </div>
    </div>
  );

  // Enhanced user data formatting with utils integration
  const formattedUser = useMemo(() => {
    try {
      console.log('🔄 Formatting user data:', user);
      const userProfile = formatUserProfile(user);
      const validatedData = validateProfileData(userProfile);
      
      // Store formatted user data for consistency
      saveUserPreferences('formattedUserData', validatedData);
      
      return {
        ...validatedData,
        displayName: sanitizeInput(user?.name || validatedData.name) || 'User',
        safeEmail: isValidEmail(user?.email || validatedData.email) ? (user?.email || validatedData.email) : 'seckin@idapps.com',
        safePhone: isValidPhoneNumber(user?.phone || validatedData.phone) ? formatPhoneNumber(user?.phone || validatedData.phone) : '+90 532 123 45 67',
        safeBio: truncateText(sanitizeInput(user?.bio || validatedData.bio), 200) || 'Teknoloji ve yenilik tutkunu bir profesyonel.',
        notes: sanitizeInput(user?.notes || validatedData.notes) || '',
        hobbies: Array.isArray(user?.hobbies) ? user.hobbies : (validatedData.hobbies || []),
        interests: Array.isArray(user?.interests) ? user.interests : (validatedData.interests || []),
        validatedBloodType: isValidBloodType(user?.bloodType || validatedData.bloodType) ? (user?.bloodType || validatedData.bloodType) : 'AB+',
        emergencyContacts: ((user?.emergencyContacts || validatedData.emergencyContacts) || []).map(contact => ({
          ...contact,
          name: sanitizeInput(contact.name),
          phone: formatPhoneNumber(contact.phone),
          relation: sanitizeInput(contact.relation)
        }))
      };
    } catch (error) {
      console.error('Error formatting user data:', error);
      return getDefaultUserData();
    }
  }, [user]);

  return (
    <div 
      ref={containerRef}
      className={`flex flex-col h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'} relative`}
    >
      {/* Main Content */}
      <main
        className={`flex-1 overflow-auto pb-navigation transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}
        style={{
          fontSize: 'calc(1rem * var(--font-size-scale))'
        }}
      >
        {/* User Profile Header */}
        <div 
          ref={headerRef}
          className={`${isDarkMode ? 'bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900' : 'bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500'} min-h-[200px] text-white p-6 relative overflow-hidden`}
        >
          <div className="flex flex-col items-center space-y-2 relative z-10">
            {/* Profile avatar - navigate to dashboard */}
            <div className="relative">
              <div 
                className={`w-24 h-24 rounded-full overflow-hidden flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} cursor-pointer shadow-lg`}
                onClick={handleProfilePhotoClick}
              >
                {user?.profilePhoto ? (
                  <img 
                    src={user.profilePhoto} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User size={64} className={`${isDarkMode ? 'text-indigo-400' : 'text-blue-600'}`} />
                )}
                {/* Small menu indicator */}
                <div className="absolute bottom-0 right-0 w-6 h-6 rounded-full flex items-center justify-center bg-white border-2 border-blue-500 shadow-md">
                  <Menu size={12} className="text-blue-600" />
                </div>
              </div>
              {/* Profile Menu Component */}
              <ProfileMenu 
                isDarkMode={isDarkMode}
                onNavigate={(tab) => {
                  if (tab === 'profileEdit') {
                    navigateToScreen('profileEdit');
                  } else if (tab === 'security') {
                    navigateToScreen('securitySettings');
                  } else if (tab === 'help') {
                    navigateToScreen('help');
                  } else if (tab === 'logout') {
                    setShowLogoutConfirm(true);
                  } else if (tab === 'settings') {
                    setShowAppearanceModal(true);
                  } else if (tab === 'identity') {
                    setActiveTab('identity');
                  }
                  setShowProfileMenu(false);
                }}
                onLogout={handleLogout}
                showProfileMenu={showProfileMenu}
                setShowProfileMenu={setShowProfileMenu}
                user={user}
              />
            </div>
            
            <h2 className="text-lg font-bold" style={{fontSize: 'calc(1.25rem * var(--font-size-scale))'}}>
              {user?.name || formattedUser?.displayName || 'Kullanıcı'}
            </h2>
            <p className="text-base text-white/80" style={{fontSize: 'calc(1rem * var(--font-size-scale))'}}>
              {user?.username || '@kullanici'}
            </p>
            
            {/* Edit profile button */}
            <button 
              className={`absolute top-4 right-4 ${isDarkMode ? 'bg-white/20' : 'bg-white/30'} p-2 rounded-full hover:bg-white/40 transition-colors`}
              onClick={handleEditProfile}
            >
              <Edit size={16} className="text-white" />
            </button>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex border-b mb-4 mt-2">
          {tabList.map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.id === 'settings') {
                  navigateToScreen('settings');
                } else if (tab.id === 'statistics') {
                  setActiveTab('statistics');
                } else if (tab.id === 'identity') {
                  setActiveTab('identity');
                }
              }}
              className={`flex-1 py-3 text-center font-semibold transition-colors text-base
                ${activeTab === tab.id
                  ? isDarkMode
                    ? 'border-b-2 border-indigo-400 text-indigo-300 bg-gray-900'
                    : 'border-b-2 border-blue-600 text-blue-600 bg-white'
                  : isDarkMode
                    ? 'text-gray-400 hover:text-indigo-200'
                    : 'text-gray-500 hover:text-blue-600'
                }`}
              style={{ outline: 'none' }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'identity' && (
            <div className="space-y-4" style={{fontSize: 'calc(1rem * var(--font-size-scale))'}}>
              {/* Enhanced Emergency Info Card with Security Features */}
              <div className={`rounded-xl overflow-hidden ${currentTheme.cardBg} ${isDarkMode ? '' : 'shadow-md'}`}>
                <div className="bg-gradient-to-r from-red-500 to-red-600 px-4 py-3 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="emergency-alert mr-2">
                        <AlertCircle size={20} />
                      </div>
                      <h3 className="font-bold text-lg">{t('dashboard.emergencyInfo', 'Acil Durum Bilgileri')}</h3>
                    </div>
                    <button 
                      onClick={handleEditEmergencyInfo}
                      className={`p-1.5 rounded-full ${currentTheme.editButton} transition-colors`}
                    >
                      <Edit size={16} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium flex items-center">
                      <Activity size={16} className="mr-2 text-red-500" />
                      {t('profile.bloodType', 'Kan Grubu')}
                    </span>
                    <span className="text-xl font-bold text-red-600 bg-red-50 dark:bg-red-900/30 px-3 py-1 rounded-lg">
                      {formattedUser?.validatedBloodType}
                    </span>
                  </div>
                  
                  {/* Allergies Section with enhanced styling */}
                  <div className={`my-4 border-t pt-4 ${currentTheme.dividerColor}`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${currentTheme.textColor}`}>
                      <AlertCircle size={16} className="mr-2 text-red-500" />
                      {t('dashboard.allergies', 'Alerjiler')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {(formattedUser?.allergies || ['Polen', 'Fıstık', 'Antibiyotik']).map((allergy, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'} border border-red-200 dark:border-red-800 transition-all hover:shadow-md`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                              <span className={`font-medium ${currentTheme.textColor}`}>{sanitizeInput(allergy)}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              allergy.toLowerCase().includes('yüksek') || allergy.toLowerCase().includes('şiddetli') 
                                ? 'bg-red-200 text-red-800 dark:bg-red-800/50 dark:text-red-200' 
                                : 'bg-orange-200 text-orange-800 dark:bg-orange-800/50 dark:text-orange-200'
                            }`}>
                              {allergy.toLowerCase().includes('yüksek') || allergy.toLowerCase().includes('şiddetli') 
                                ? t('health.severe', 'Şiddetli') 
                                : t('health.moderate', 'Orta')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Health Conditions with enhanced styling */}
                  <div className={`my-4 border-t pt-4 ${currentTheme.dividerColor}`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${currentTheme.textColor}`}>
                      <Activity size={16} className="mr-2 text-blue-500" />
                      {t('dashboard.healthConditions', 'Sağlık Durumu')}
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {(formattedUser?.conditions || ['Hipertansiyon', 'Astım']).map((condition, idx) => (
                        <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-200 dark:border-blue-800 transition-all hover:shadow-md`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Activity size={16} className="text-blue-500" />
                              <span className={`font-medium ${currentTheme.textColor}`}>{sanitizeInput(condition)}</span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full bg-blue-200 text-blue-800 dark:bg-blue-800/50 dark:text-blue-200`}>
                              {t('health.chronic', 'Kronik')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Medications with enhanced layout */}
                  <div className={`my-4 border-t pt-4 ${currentTheme.dividerColor}`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${currentTheme.textColor}`}>
                      <Heart size={16} className="mr-2 text-green-500" />
                      {t('dashboard.medications', 'İlaçlar')}
                    </h4>
                    <div className="space-y-3">
                      {(formattedUser?.medications || ['Lisinopril 10mg (günde 1)', 'Ventolin inhaler (gerektiğinde)']).map((medication, idx) => (
                        <div key={idx} className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'} border border-green-200 dark:border-green-800 transition-all hover:shadow-md`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-3">
                              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                              <div>
                                <span className={`font-medium ${currentTheme.textColor} block`}>
                                  {sanitizeInput(medication.split(' ')[0])} {/* Medication name */}
                                </span>
                                <span className={`text-sm ${currentTheme.textSecondary}`}>
                                  {sanitizeInput(medication.split(' ').slice(1).join(' '))} {/* Dosage and frequency */}
                                </span>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full bg-green-200 text-green-800 dark:bg-green-800/50 dark:text-green-200`}>
                              {medication.toLowerCase().includes('gerektiğinde') ? t('health.asNeeded', 'Gerektiğinde') : t('health.daily', 'Günlük')}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t('health.lastTaken', 'Son alım')}: {t('health.today', 'Bugün')} 08:00
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Chronic Diseases Section */}
                  {formattedUser?.chronicDiseases && formattedUser.chronicDiseases.length > 0 && (
                    <div className={`my-4 border-t pt-4 ${currentTheme.dividerColor}`}>
                      <h4 className={`font-semibold mb-3 flex items-center ${currentTheme.textColor}`}>
                        <Shield size={16} className="mr-2 text-purple-500" />
                        {t('dashboard.chronicDiseases', 'Kronik Hastalıklar')}
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {formattedUser.chronicDiseases.map((disease, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-50'} border border-purple-200 dark:border-purple-800 transition-all hover:shadow-md`}>
                            <div className="flex items-center space-x-3">
                              <Shield size={16} className="text-purple-500" />
                              <span className={`font-medium ${currentTheme.textColor}`}>{sanitizeInput(disease)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Enhanced Emergency Contacts Card */}
              <div className={`rounded-xl overflow-hidden ${currentTheme.cardBg} ${isDarkMode ? '' : 'shadow-md'}`}>
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-3 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Phone size={20} className="mr-2" />
                      <h3 className="font-bold text-lg">{t('dashboard.emergencyContacts', 'Acil Durum Kişileri')}</h3>
                    </div>
                    <button 
                      onClick={handleEditEmergencyContacts}
                      className={`p-1.5 rounded-full ${currentTheme.editButton} transition-colors`}
                    >
                      <Edit size={16} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  {(formattedUser?.emergencyContacts || getDefaultEmergencyContacts()).map((contact, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md hover:scale-[1.02] ${idx > 0 ? 'mt-3' : ''}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            contact.relationship === 'Doktor' || contact.relation === 'Doktor' 
                              ? 'bg-green-100 dark:bg-green-900/30' 
                              : idx === 0 
                                ? 'bg-red-100 dark:bg-red-900/30' 
                                : 'bg-blue-100 dark:bg-blue-900/30'
                          }`}>
                            <User size={18} className={
                              contact.relationship === 'Doktor' || contact.relation === 'Doktor' 
                                ? 'text-green-600 dark:text-green-400' 
                                : idx === 0 
                                  ? 'text-red-600 dark:text-red-400' 
                                  : 'text-blue-600 dark:text-blue-400'
                            } />
                          </div>
                          <div>
                            <h5 className={`font-semibold ${currentTheme.textColor}`}>
                              {sanitizeInput(contact.name)}
                            </h5>
                            <span className={`text-sm px-2 py-1 rounded-full ${
                              contact.relationship === 'Doktor' || contact.relation === 'Doktor' 
                                ? currentTheme.badgeColors.green 
                                : idx === 0 
                                  ? currentTheme.badgeColors.red 
                                  : currentTheme.badgeColors.blue
                            }`}>
                              {sanitizeInput(contact.relationship || contact.relation)}
                            </span>
                          </div>
                        </div>
                        {(contact.isPrimary || idx === 0) && (
                          <div className="flex items-center space-x-1">
                            <Star size={14} className="text-yellow-500 fill-current" />
                            <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">
                              {t('profile.primary', 'Birincil')}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center space-x-3">
                          <Phone size={14} className={currentTheme.iconColor} />
                          <span className={`font-mono text-sm ${currentTheme.textColor}`}>
                            {contact.phone}
                          </span>
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs text-green-600 dark:text-green-400">
                              {t('profile.verified', 'Doğrulandı')}
                            </span>
                          </div>
                        </div>
                        
                        {contact.location && (
                          <div className="flex items-center space-x-3">
                            <MapPin size={14} className={currentTheme.iconColor} />
                            <span className={`text-sm ${currentTheme.textSecondary}`}>
                              {sanitizeInput(contact.location)}
                            </span>
                          </div>
                        )}
                        
                        {/* Contact actions */}
                        <div className="flex items-center space-x-2 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                          <button 
                            onClick={() => window.open(`tel:${contact.phone.replace(/\s+/g, '')}`, '_self')}
                            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                              isDarkMode 
                                ? 'bg-green-600 hover:bg-green-700 text-white' 
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                          >
                            {t('profile.call', 'Ara')}
                          </button>
                          <button className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                            isDarkMode 
                              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                              : 'bg-blue-500 hover:bg-blue-600 text-white'
                          }`}>
                            {t('profile.message', 'Mesaj')}
                          </button>
                          {(contact.relationship === 'Doktor' || contact.relation === 'Doktor') && (
                            <button className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                              isDarkMode 
                                ? 'bg-purple-600 hover:bg-purple-700 text-white' 
                                : 'bg-purple-500 hover:bg-purple-600 text-white'
                            }`}>
                              {t('profile.appointment', 'Randevu')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button 
                    onClick={handleAddEmergencyContact}
                    className={`mt-4 w-full py-3 border-2 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-[1.02] ${
                      isDarkMode 
                        ? 'border-blue-500 text-blue-300 hover:bg-blue-500/20 hover:border-blue-400' 
                        : 'border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600'
                    }`}
                  >
                    <Plus size={18} className="mr-2" />
                    {t('dashboard.addEmergencyContact', 'Acil Durum Kişisi Ekle')}
                  </button>
                </div>
              </div>
              
              {/* Enhanced Personal Info Card */}
              <div className={`rounded-xl overflow-hidden ${currentTheme.cardBg} ${isDarkMode ? '' : 'shadow-md'}`}>
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-4 py-3 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <User size={20} className="mr-2" />
                      <h3 className="font-bold text-lg">{t('dashboard.personalInfo', 'Kişisel Bilgiler')}</h3>
                    </div>
                    <button 
                      onClick={handleEditPersonalInfo}
                      className={`p-1.5 rounded-full ${currentTheme.editButton} transition-colors`}
                    >
                      <Edit size={16} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {/* Languages section */}
                  <div className={`mb-4 pb-4 border-b ${currentTheme.dividerColor}`}>
                    <h4 className={`mb-3 font-semibold flex items-center ${currentTheme.textColor}`}>
                      <Globe size={16} className="mr-2" />
                      {t('profile.languages', 'Konuşulan Diller')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {(formattedUser?.languages || ['Türkçe', 'English', 'Deutsch']).map((language, idx) => (
                        <span key={idx} className={`px-3 py-1.5 rounded-full text-xs font-medium ${currentTheme.badgeColors.teal} transition-transform hover:scale-105`}>
                          {sanitizeInput(language)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Hobbies section */}
                  {formattedUser?.hobbies && formattedUser.hobbies.length > 0 && (
                    <div className={`mb-4 pb-4 border-b ${currentTheme.dividerColor}`}>
                      <h4 className={`mb-3 font-semibold flex items-center ${currentTheme.textColor}`}>
                        <Heart size={16} className="mr-2" />
                        {t('profile.hobbies', 'Hobiler')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {formattedUser.hobbies.map((hobby, idx) => (
                          <span key={idx} className={`px-3 py-1.5 rounded-full text-xs font-medium ${currentTheme.badgeColors.green} transition-transform hover:scale-105`}>
                            {sanitizeInput(hobby)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Interests section */}
                  {formattedUser?.interests && formattedUser.interests.length > 0 && (
                    <div className={`mb-4 pb-4 border-b ${currentTheme.dividerColor}`}>
                      <h4 className={`mb-3 font-semibold flex items-center ${currentTheme.textColor}`}>
                        <Settings size={16} className="mr-2" />
                        {t('profile.interests', 'İlgi Alanları')}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {formattedUser.interests.map((interest, idx) => (
                          <span key={idx} className={`px-3 py-1.5 rounded-full text-xs font-medium ${currentTheme.badgeColors.purple} transition-transform hover:scale-105`}>
                            {sanitizeInput(interest)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Enhanced contact information */}
                  <div className={`mb-4 pb-4 border-b ${currentTheme.dividerColor}`}>
                    <h4 className={`mb-3 font-semibold flex items-center ${currentTheme.textColor}`}>
                      <Mail size={16} className="mr-2" />
                      {t('profile.contacts', 'İletişim Bilgileri')}
                    </h4>
                    <div className="space-y-3">
                      {(formattedUser?.phones || [formattedUser?.safePhone]).map((phone, idx) => (
                        <div key={`phone-${idx}`} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                          <div className="flex items-center space-x-3">
                            <Phone size={16} className={currentTheme.iconColor} />
                            <span className={`font-medium ${currentTheme.textColor}`}>{phone}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {isValidPhoneNumber(phone) && (
                              <span className={`text-xs px-2 py-1 rounded ${currentTheme.badgeColors.green}`}>
                                Doğrulandı
                              </span>
                            )}
                            <button 
                              onClick={() => navigator.clipboard?.writeText(phone)}
                              className={`text-xs px-2 py-1 rounded ${currentTheme.badgeColors.blue} hover:scale-105 transition-transform`}
                              aria-label={`${phone} numarasını kopyala`}
                            >
                              Kopyala
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {(formattedUser?.emails || [formattedUser?.safeEmail]).map((email, idx) => (
                        <div key={`email-${idx}`} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                          <div className="flex items-center space-x-3">
                            <Mail size={16} className={currentTheme.iconColor} />
                            <span className={`font-medium ${currentTheme.textColor} text-sm`}>{email}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            {isValidEmail(email) && (
                              <span className={`text-xs px-2 py-1 rounded ${currentTheme.badgeColors.green}`}>
                                Doğrulandı
                              </span>
                            )}
                            <button 
                              onClick={() => navigator.clipboard?.writeText(email)}
                              className={`text-xs px-2 py-1 rounded ${currentTheme.badgeColors.blue} hover:scale-105 transition-transform`}
                              aria-label={`${email} adresini kopyala`}
                            >
                              Kopyala
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      <div className={`flex items-center space-x-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                        <MapPin size={16} className={currentTheme.iconColor} />
                        <span className={`font-medium ${currentTheme.textColor}`}>{formattedUser?.address || 'İstanbul, Türkiye'}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Bio Section */}
                  <div>
                    <h4 className={`mb-3 font-semibold flex items-center ${currentTheme.textColor}`}>
                      <User size={16} className="mr-2" />
                      {t('profile.bio', 'Hakkımda')}
                    </h4>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                      <p className={`text-sm leading-relaxed ${currentTheme.textSecondary}`}>
                        {formattedUser?.safeBio}
                      </p>
                    </div>
                  </div>

                  {/* Additional Notes Section */}
                  {formattedUser?.notes && (
                    <div>
                      <h4 className={`mb-3 font-semibold flex items-center ${currentTheme.textColor}`}>
                        <FileText size={16} className="mr-2" />
                        {t('profile.additionalNotes', 'Ek Notlar')}
                      </h4>
                      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                        <p className={`text-sm leading-relaxed ${currentTheme.textSecondary}`}>
                          {sanitizeInput(formattedUser.notes)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Physical Information Card */}
              <div className={`rounded-xl overflow-hidden ${currentTheme.cardBg} ${isDarkMode ? '' : 'shadow-md'}`}>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Activity size={20} className="mr-2" />
                      <h3 className="font-bold text-lg">{t('profile.physical', 'Fiziksel Bilgiler')}</h3>
                    </div>
                    <button 
                      onClick={handleEditPhysicalInfo}
                      className={`p-1.5 rounded-full ${currentTheme.editButton} transition-colors`}
                    >
                      <Edit size={16} className="text-white" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {/* Primary physical stats layout */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-transform hover:scale-105`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Activity size={16} className={`${currentTheme.iconColor} text-purple-500`} />
                        <span className={`text-sm font-medium ${currentTheme.textMuted}`}>{t('profile.height', 'Boy')}</span>
                      </div>
                      <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                        {formattedUser?.height ? 
                          (formattedUser.height.toString().includes('cm') ? formattedUser.height : `${formattedUser.height} cm`) : 
                          '185 cm'
                        }
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-transform hover:scale-105`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Activity size={16} className={`${currentTheme.iconColor} text-purple-500`} />
                        <span className={`text-sm font-medium ${currentTheme.textMuted}`}>{t('profile.weight', 'Kilo')}</span>
                      </div>
                      <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                        {formattedUser?.weight ? 
                          (formattedUser.weight.toString().includes('kg') ? formattedUser.weight : `${formattedUser.weight} kg`) : 
                          '80 kg'
                        }
                      </div>
                    </div>
                  </div>
                  
                  {/* Blood type prominently displayed */}
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'} border border-red-200 dark:border-red-800 mb-4`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Heart size={20} className="text-red-500" />
                        <span className={`text-lg font-semibold ${currentTheme.textColor}`}>{t('profile.bloodType', 'Kan Grubu')}</span>
                      </div>
                      <div className="text-2xl font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/50 px-4 py-2 rounded-lg">
                        {formattedUser?.validatedBloodType || 'A Rh+'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Physical characteristics grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-green-400"></div>
                          <span className={`text-sm font-medium ${currentTheme.textMuted}`}>{t('profile.eyeColor', 'Göz Rengi')}</span>
                        </div>
                        <span className={`text-sm font-bold ${currentTheme.textColor} capitalize`}>
                          {sanitizeInput(formattedUser?.eyeColor) || 'Kahverengi'}
                        </span>
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} transition-all hover:shadow-md`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-amber-400 to-orange-400"></div>
                          <span className={`text-sm font-medium ${currentTheme.textMuted}`}>{t('profile.hairColor', 'Saç Rengi')}</span>
                        </div>
                        <span className={`text-sm font-bold ${currentTheme.textColor} capitalize`}>
                          {sanitizeInput(formattedUser?.hairColor) || 'Siyah'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* BMI Calculation if height and weight are available */}
                  {formattedUser?.height && formattedUser?.weight && (
                    <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'} border border-blue-200 dark:border-blue-800`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <BarChart size={16} className="text-blue-500" />
                          <span className={`text-sm font-medium ${currentTheme.textMuted}`}>BMI</span>
                        </div>
                        <span className={`text-sm font-bold ${currentTheme.textColor}`}>
                          {(() => {
                            const height = parseFloat(formattedUser.height) / 100; // Convert cm to meters
                            const weight = parseFloat(formattedUser.weight);
                            const bmi = weight / (height * height);
                            return isNaN(bmi) ? 'N/A' : bmi.toFixed(1);
                          })()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced QR Identity Card with Security */}
              <div className={`rounded-xl overflow-hidden ${currentTheme.cardBg} ${isDarkMode ? '' : 'shadow-md'}`}>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-4 py-3 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Heart size={20} className="mr-2" />
                      <h3 className="font-bold text-lg">{t('dashboard.qrIdentity', 'QR Kimlik')}</h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                        {t('security.secure', 'Güvenli')}
                      </span>
                      <Shield size={16} className="text-green-300" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} border ${currentTheme.cardBorder}`}>
                    <QRCode 
                      data={`https://idapps.io/id/${user?.id || '12345'}`}
                      name={formattedUser?.displayName}
                      id={`#ID${user?.id || '12345'}`}
                      size="medium"
                      isDarkMode={isDarkMode}
                    />
                  </div>
                  
                  {/* Enhanced QR info section */}
                  <div className="mt-4 space-y-3">
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'} border ${isDarkMode ? 'border-green-800/50' : 'border-green-200'}`}>
                      <div className="flex items-center space-x-2 mb-1">
                        <Shield size={14} className="text-green-500" />
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
                          {t('security.encrypted', 'Şifrelenmiş')}
                        </span>
                      </div>
                      <p className={`text-xs ${isDarkMode ? 'text-green-200' : 'text-green-700'}`}>
                        {t('profile.qrDescription', 'Acil durumlarda kimlik bilgilerinize güvenli erişim için QR kodunuzu kullanın')}
                      </p>
                    </div>
                    
                    {/* QR Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} text-center`}>
                        <div className={`text-lg font-bold ${currentTheme.textColor}`}>
                          {getFromStorage('qr_scan_count', 0)}
                        </div>
                        <div className={`text-xs ${currentTheme.textMuted}`}>
                          {t('dashboard.totalScans', 'Toplam Tarama')}
                        </div>
                      </div>
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'} border ${currentTheme.cardBorder} text-center`}>
                        <div className={`text-lg font-bold ${currentTheme.textColor}`}>
                          {formatDate(getFromStorage('last_qr_update', new Date()), 'short')}
                        </div>
                        <div className={`text-xs ${currentTheme.textMuted}`}>
                          {t('profile.lastUpdate', 'Son Güncelleme')}
                        </div>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => handleShowSecurityVerification(() => {
                        saveToStorage('qr_regenerated', Date.now());
                        console.log('QR Code regenerated');
                        // QR yenileme logic burada olacak
                      })}
                      className={`w-full py-3 px-4 rounded-lg ${currentTheme.buttonBg} ${currentTheme.buttonHover} text-white font-medium flex items-center justify-center transition-colors`}
                    >
                      <Activity size={16} className="mr-2" />
                      {t('security.regenerateQR', 'QR Kodu Yenile')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'statistics' && (
            <div className="mb-4">
              <div className="mb-4">
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  İstatistikleriniz ve sosyal etkileşimleriniz burada görüntülenir. Sosyal XP, milestones, görevler ve rozetler ile Duolingo tarzı oyun unsurlarından faydalanın. Dijital denge metriklerinizi takip ederek "kendi hayatınızın influencer'ı" olun ve gerçek hayat başarımlarınızla sosyal medya tüketiminizi karşılaştırın.
                </p>
              </div>
              <StatisticsPanel 
                isDarkMode={isDarkMode} 
                initialTab={screenParams?.initialTab === "digital-discipline" ? "digital-discipline" : "overview"}
              />
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-4">
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl`}>
                <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('settings.accountSettings', 'Hesap Ayarları')}</h3>
                </div>
                <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <button
                    className={`w-full flex justify-between items-center p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    onClick={handleAccountSettings}
                  >
                    <div className="flex items-center space-x-3">
                      <User size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{t('profile.editProfile', 'Profil Bilgilerini Düzenle')}</span>
                    </div>
                    <ChevronRight size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                  </button>
                  <button
                    className={`w-full flex justify-between items-center p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    onClick={() => navigateToScreen('accountInfo')}
                  >
                    <div className="flex items-center space-x-3">
                      <User size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{t('account.information', 'Hesap Bilgileri')}</span>
                    </div>
                    <ChevronRight size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                  </button>
                  <button
                    className={`w-full flex justify-between items-center p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    onClick={handleSecuritySettings}
                  >
                    <div className="flex items-center space-x-3">
                      <Shield size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{t('security.security', 'Güvenlik')}</span>
                    </div>
                    <ChevronRight size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                  </button>
                </div>
              </div>

              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl`}>
                <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('settings.appSettings', 'Uygulama Ayarları')}</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{t('settings.appSettingsDesc', 'Uygulama davranışı ve tercihlerini yönet')}</p>
                </div>
                <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {/* Theme and Appearance */}
                  <button 
                    className={`w-full flex justify-between items-center p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    onClick={handleAppSettings}
                  >
                    <div className="flex items-center space-x-3">
                      {isDarkMode ? <Moon size={18} className="text-gray-300" /> : <Sun size={18} className="text-gray-500" />}
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{t('settings.themeAndAppearance', 'Tema ve Görünüm')}</span>
                    </div>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {isDarkMode ? t('settings.darkMode', 'Karanlık Mod') : t('settings.lightMode', 'Aydınlık Mod')}
                    </span>
                  </button>
                  
                  {/* Language and Region */}
                  <button 
                    className={`w-full flex justify-between items-center p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    onClick={handleLanguageAndRegion}
                  >
                    <div className="flex items-center space-x-3">
                      <Globe size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{t('settings.languageAndRegion', 'Dil ve Bölge')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>{currentLang.name}</span>
                      <ChevronRight size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                    </div>
                  </button>
                </div>
              </div>

              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl`}>
                <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('settings.other', 'Diğer')}</h3>
                </div>
                <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <button 
                    className={`w-full flex justify-between items-center p-4 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors`}
                    onClick={handleHelpAndSupport}
                  >
                    <div className="flex items-center space-x-3">
                      <HelpCircle size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                      <span className={isDarkMode ? 'text-gray-200' : 'text-gray-700'}>{t('settings.helpAndSupport', 'Yardım ve Destek')}</span>
                    </div>
                    <ChevronRight size={18} className={isDarkMode ? 'text-gray-300' : 'text-gray-500'} />
                  </button>
                  <button 
                    className={`w-full flex items-center space-x-3 p-4 ${isDarkMode ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'} transition-colors`}
                    onClick={() => setShowLogoutConfirm(true)}
                  >
                    <LogOut size={18} />
                    <span>{t('nav.logout', 'Çıkış Yap')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation activeItem="profile" isDarkMode={isDarkMode} />

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg max-w-sm w-full p-6 space-y-4`}>
            <h3 className={`font-bold text-lg text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('settings.confirmLogout', 'Do you want to logout?')}</h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-center`}>
              {t('settings.logoutDescription', 'You are about to logout from your account. You will need to login again.')}
            </p>
            <div className="flex space-x-3">
              <button 
                className={`flex-1 py-3 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button 
                className={`flex-1 py-3 text-white rounded-lg font-medium ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'} transition-all`}
                onClick={handleConfirmLogout}
              >
                {t('nav.logout', 'Logout')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl max-w-sm w-full p-6`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('settings.languageSelection', 'Language Selection')}</h3>
              <button 
                className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                onClick={() => setShowLanguageModal(false)}
              >
                <X size={20} className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} />
              </button>
            </div>
            
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
              {t('settings.selectLanguageDescription', 'Select the language you want to use the app in.')}
            </p>
            
            <div className="grid grid-cols-1 gap-2 mb-4">
              {languages.map(lang => {
                const isActive = currentLanguage === lang.code;
                const buttonClass = isActive
                  ? isDarkMode 
                    ? 'border-indigo-500 bg-indigo-900/20 text-indigo-300' 
                    : 'border-blue-500 bg-blue-50 text-blue-700'
                  : isDarkMode 
                    ? 'border-gray-700 hover:bg-gray-700 text-gray-200' 
                    : 'border-gray-200 hover:bg-gray-50 text-gray-700';
                  
                const handleClick = () => {
                  setShowLanguageModal(false);
                  setTimeout(() => {
                    changeLanguage(lang.code);
                  }, 10);
                };
                  
                return (
                  <button
                    key={lang.code}
                    className={`flex items-center justify-between p-3 rounded-lg border ${buttonClass} transition-colors`}
                    onClick={handleClick}
                  >
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{lang.flag}</span>
                      <span className="font-medium">{lang.name}</span>
                    </div>
                    
                    {isActive && (
                      <Check className={`h-5 w-5 ${isDarkMode ? 'text-indigo-300' : 'text-blue-500'}`} />
                    )}
                  </button>
                );
              })}
            </div>
            
            <button
              className={`w-full py-3 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'} transition-colors`}
              onClick={() => setShowLanguageModal(false)}
            >
              {t('common.close', 'Close')}
            </button>
          </div>
        </div>
      )}
      
      {/* Appearance Settings Modal */}
      {renderAppearanceModal()}
      
      {/* Security Verification Modal */}
      {showSecurity && <SecurityVerification />}
    </div>
  );
};

// HOC wrap component
export default withTranslation(ProfileScreen);